import { Box } from "@mui/material";
import { type FC, Suspense, useState } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ERROR_SEVERITY_PROPERTIES, trackErrorEvent } from "@/utils/analytics";
import { retryingLazy } from "@/utils/retryingLazy";

const CHECKMARK_SIZE = { width: 120, height: 120 };
const CHECKMARK_PLACEHOLDER = <Box sx={CHECKMARK_SIZE} />;

const successAnimation = retryingLazy(
	() => import("./SuccessCheckAnimation"),
	({ SuccessCheckAnimation }) => SuccessCheckAnimation,
);

/**
 * Fetches the animation ahead of the first checkmark. Hosts call it on mount because they dismiss the
 * checkmark on a timer, and a first one that waited for the download would show blank or cut off.
 */
export const preloadCheckMark = (): void => {
	// The checkmark itself loads again, or degrades to its box, when it is shown.
	successAnimation.load().catch((error: unknown) => trackErrorEvent("checkmark_preload_failed", error, ERROR_SEVERITY_PROPERTIES));
};

// An animation that failed to download degrades to its empty box rather than blanking the page around it.
export const CheckMark: FC = () => {
	// Taken once per mount: a load that fails while this one is shown must not swap the component under it.
	const [SuccessAnimation] = useState(() => successAnimation.current());
	return (
		<ErrorBoundary fallback={CHECKMARK_PLACEHOLDER}>
			<Suspense fallback={CHECKMARK_PLACEHOLDER}>
				<SuccessAnimation style={CHECKMARK_SIZE} />
			</Suspense>
		</ErrorBoundary>
	);
};
